import type {
  AgentIntakeWarning,
  AgentExecuteRepoSignals,
  AgentScopeAnswers,
  AgentSuperMemoryEntry,
  PromptScopeSuggestion,
  PromptSpecialization,
} from "../../types/agent";

const GENERIC_INITIAL_PROMPTS = new Set([
  "fix it",
  "fix this",
  "optimize",
  "improve",
  "help",
  "debug",
  "secure it",
  "speed up",
  "make it better",
]);

const FILE_PATH_PATTERN =
  /\b(?:src|app|pages|components|lib|server|api|packages|tests?|__tests__|hooks?|services?|plugins?|scripts?)\/[^\s"'`]+/gi;

const STACK_HINT_PATTERNS: Array<{ key: string; pattern: RegExp }> = [
  { key: "web", pattern: /\b(next\.js|react|vue|angular|vite|webpack|html|css|frontend|browser)\b/i },
  { key: "mobile", pattern: /\b(react native|flutter|ios|android|mobile)\b/i },
  { key: "backend", pattern: /\b(api|backend|server|express|nestjs|database|postgres|mysql)\b/i },
  { key: "python", pattern: /\b(python|pytest|pyproject|requirements\.txt)\b/i },
  { key: "java", pattern: /\b(java|spring|maven|pom\.xml|gradle)\b/i },
  { key: "c_cpp", pattern: /\b(c\+\+|cmake|makefile|gcc|clang)\b/i },
  { key: "dotnet", pattern: /\b(dotnet|\.net|c#|csproj|sln)\b/i },
  { key: "php", pattern: /\b(php|composer)\b/i },
  { key: "ruby", pattern: /\b(ruby|rails|gemfile|rspec)\b/i },
  { key: "shopify", pattern: /\b(shopify|theme\.liquid)\b/i },
  { key: "desktop", pattern: /\b(desktop|tauri|electron)\b/i },
];

const AREA_HINT_PATTERNS: Array<{ key: string; pattern: RegExp }> = [
  { key: "api", pattern: /\b(api|endpoint|route|controller|handler)\b/i },
  { key: "frontend", pattern: /\b(component|frontend|render|view|ui)\b/i },
  { key: "auth", pattern: /\b(auth|oauth|token|session|permission)\b/i },
  { key: "build", pattern: /\b(build|bundle|compile|webpack|vite|maven|gradle|cmake|make)\b/i },
  { key: "testing", pattern: /\b(test|pytest|jest|vitest|rspec)\b/i },
  { key: "security", pattern: /\b(security|xss|csrf|sanitize|injection|vulnerability)\b/i },
  { key: "performance", pattern: /\b(performance|lcp|cls|ttfb|latency|slow|cpu|memory|cache)\b/i },
];

export interface ScopeSuggestionAnswerOptions {
  goal: string[];
  targetArea: string[];
  verification: string[];
}

export interface ScopeSuggestionBuildResult {
  specialization: PromptSpecialization;
  suggestions: PromptScopeSuggestion[];
  requiredHints: string[];
  memoryDerived: boolean;
  answerOptions: ScopeSuggestionAnswerOptions;
}

function normalizeText(value: string): string {
  return String(value || "").trim().toLowerCase();
}

function collectUnique(values: string[], limit: number): string[] {
  const seen = new Set<string>();
  const output: string[] = [];
  for (const value of values) {
    const normalized = value.trim();
    if (!normalized) continue;
    const key = normalized.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    output.push(normalized);
    if (output.length >= limit) break;
  }
  return output;
}

function countKeywordHits(text: string, keywords: string[]): number {
  let hits = 0;
  for (const keyword of keywords) {
    if (text.includes(keyword)) hits += 1;
  }
  return hits;
}

function inferStackHintsFromRepoSignals(repoSignals?: AgentExecuteRepoSignals): string[] {
  if (!repoSignals) return [];
  const corpus = [
    ...(Array.isArray(repoSignals.filesSample) ? repoSignals.filesSample.slice(0, 80) : []),
    ...(Array.isArray(repoSignals.frameworkHints) ? repoSignals.frameworkHints.slice(0, 20) : []),
  ].join(" ");
  const stackHints: string[] = [];
  for (const hint of STACK_HINT_PATTERNS) {
    if (hint.pattern.test(corpus)) {
      stackHints.push(hint.key);
    }
  }
  return collectUnique(stackHints, 4);
}

function extractMemoryHints(params: {
  memoryEntries?: AgentSuperMemoryEntry[];
  repoFullName?: string;
  prompt?: string;
}) {
  const entries = Array.isArray(params.memoryEntries) ? params.memoryEntries.slice(0, 120) : [];
  const targetRepo = normalizeText(params.repoFullName || "");
  const promptTokens = normalizeText(params.prompt || "")
    .split(/[^a-z0-9_./-]+/g)
    .filter((token) => token.length >= 3)
    .slice(0, 20);
  const ranked = entries
    .map((entry) => {
      const content = String(entry.content || "").trim();
      if (!content) return null;
      const repo = normalizeText(String(entry.repo || ""));
      const source = normalizeText(String(entry.source || ""));
      const haystack = `${content} ${source}`.toLowerCase();
      let score = 0;
      const repoExact = !!targetRepo && repo === targetRepo;
      if (repoExact) score += 10;
      else if (!repo) score += 1;
      if (typeof entry.confidence === "number") score += Math.max(0, Math.min(1, entry.confidence)) * 2;
      if (typeof entry.successCount === "number") score += Math.min(5, Math.max(0, entry.successCount)) * 0.5;
      if (typeof entry.failureCount === "number") score -= Math.min(5, Math.max(0, entry.failureCount)) * 0.75;
      let tokenHits = 0;
      for (const token of promptTokens) {
        if (haystack.includes(token)) tokenHits += 1;
      }
      score += Math.min(tokenHits, 8);
      if (/\b(success|resolved|fixed|closed|merged|completed)\b/i.test(haystack)) score += 1;
      return { entry, content, score };
    })
    .filter((value): value is { entry: AgentSuperMemoryEntry; content: string; score: number } => !!value)
    .sort((a, b) => b.score - a.score)
    .slice(0, 8);

  const fileAnchors: string[] = [];
  const areaHints: string[] = [];
  const stackHints: string[] = [];

  for (const item of ranked) {
    const corpus = `${item.content} ${String(item.entry.source || "")}`;
    fileAnchors.push(...(item.entry.fileAnchors || []));
    areaHints.push(...(item.entry.issueCategories || []));
    stackHints.push(...(item.entry.stackHints || []));
    const matches = corpus.match(FILE_PATH_PATTERN);
    if (Array.isArray(matches)) {
      for (const match of matches) {
        fileAnchors.push(match.trim().replace(/[),.;:]+$/, ""));
      }
    }
    for (const area of AREA_HINT_PATTERNS) {
      if (area.pattern.test(corpus)) {
        areaHints.push(area.key);
      }
    }
    for (const hint of STACK_HINT_PATTERNS) {
      if (hint.pattern.test(corpus)) {
        stackHints.push(hint.key);
      }
    }
  }

  return {
    memoryDerived: ranked.length > 0 && (fileAnchors.length > 0 || areaHints.length > 0 || stackHints.length > 0),
    fileAnchors: collectUnique(fileAnchors, 4),
    areaHints: collectUnique(areaHints, 6),
    stackHints: collectUnique(stackHints, 4),
  };
}

export function detectPromptSpecialization(text: string): PromptSpecialization {
  const corpus = normalizeText(text);
  const performanceScore = countKeywordHits(corpus, [
    "performance",
    "optimize",
    "pagespeed",
    "lcp",
    "cls",
    "bundle",
    "latency",
    "cache",
  ]);
  const securityScore = countKeywordHits(corpus, [
    "security",
    "xss",
    "csrf",
    "auth",
    "token",
    "session",
    "vulnerability",
    "sanitize",
  ]);
  const debuggingScore = countKeywordHits(corpus, [
    "bug",
    "debug",
    "error",
    "failing",
    "failure",
    "exception",
    "crash",
    "not working",
  ]);
  const stabilityScore = countKeywordHits(corpus, [
    "stability",
    "reliability",
    "resilience",
    "flaky",
    "flakiness",
    "regression",
    "robustness",
    "consistency",
    "race condition",
    "deadlock",
  ]);

  const ranked = [
    { key: "performance" as const, score: performanceScore },
    { key: "security" as const, score: securityScore },
    { key: "debugging" as const, score: debuggingScore },
    { key: "stability" as const, score: stabilityScore },
  ].sort((a, b) => b.score - a.score);

  if (ranked[0].score === 0) return "mixed";
  if (ranked[1].score > 0 && ranked[0].score - ranked[1].score <= 1) return "mixed";
  return ranked[0].key;
}

export function isLikelyVaguePrompt(prompt: string): boolean {
  const normalized = normalizeText(prompt);
  if (!normalized) return true;
  const normalizedNoPunctuation = normalized.replace(/[^\w\s]/g, " ").replace(/\s+/g, " ").trim();
  if (GENERIC_INITIAL_PROMPTS.has(normalizedNoPunctuation)) return true;
  const tokenCount = normalizedNoPunctuation ? normalizedNoPunctuation.split(/\s+/).length : 0;
  const hasArtifact =
    /\b(?:src|app|pages|components|lib|server|api|tests?)\/[^\s"'`]+\.[a-z0-9]+\b/i.test(prompt) ||
    /\b(?:typeerror|referenceerror|syntaxerror|error:|failed|exception)\b/i.test(prompt) ||
    /\b\/api\/[a-z0-9/_-]+\b/i.test(prompt) ||
    /\b[a-zA-Z_$][\w$]*\s*\(/.test(prompt);
  const hasMetric =
    /\b(?:lcp|cls|fcp|ttfb|tti|latency|memory|cpu|bundle|vitals?)\b/i.test(prompt) ||
    /\b\d+(?:\.\d+)?\s*(?:ms|s|kb|mb|%)\b/i.test(prompt);
  if (tokenCount <= 4 && !hasArtifact && !hasMetric) return true;
  return tokenCount < 10 && !hasArtifact && !hasMetric;
}

function buildRequiredHints(specialization: PromptSpecialization): string[] {
  const base = [
    "Describe the exact symptom or failing behavior.",
    "Point to a target file/module/component/endpoint to inspect first.",
    "State the expected outcome after the fix.",
  ];
  if (specialization === "performance") {
    return [base[0], base[1], "Include a metric goal (for example LCP/TTFB or bundle size)."];
  }
  if (specialization === "security") {
    return [base[0], base[1], "Name the vulnerability class or security concern to prioritize."];
  }
  if (specialization === "debugging") {
    return [base[0], base[1], "Include error text, stack trace, or reproduction steps if available."];
  }
  if (specialization === "stability") {
    return [base[0], base[1], "Call out flaky/reliability symptoms and the consistency goal you expect."];
  }
  return base;
}

function buildVerificationOptions(stackHints: string[]): string[] {
  const options = ["tests", "build", "lint", "smoke"];
  if (stackHints.includes("python")) options.unshift("pytest");
  if (stackHints.includes("java")) options.unshift("maven/gradle tests");
  if (stackHints.includes("dotnet")) options.unshift("dotnet test");
  if (stackHints.includes("c_cpp")) options.unshift("cmake/make build");
  return collectUnique(options, 6);
}

function buildGoalHint(scopeAnswers?: AgentScopeAnswers): string {
  if (!scopeAnswers?.goal) return "";
  return ` Primary goal: ${scopeAnswers.goal}.`;
}

function buildTargetHint(params: {
  scopeAnswers?: AgentScopeAnswers;
  fileAnchors: string[];
  areaHints: string[];
}): string {
  if (params.scopeAnswers?.targetArea) {
    return ` Start in ${params.scopeAnswers.targetArea}.`;
  }
  if (params.fileAnchors.length > 0) {
    return ` Start with ${params.fileAnchors.slice(0, 2).join(", ")}.`;
  }
  if (params.areaHints.length > 0) {
    return ` Start with the ${params.areaHints[0]} area first.`;
  }
  return "";
}

function buildVerificationHint(scopeAnswers?: AgentScopeAnswers, fallback = "available tests/build commands"): string {
  if (scopeAnswers?.verification) {
    return ` Verify with ${scopeAnswers.verification}.`;
  }
  return ` Verify with ${fallback}.`;
}

export function buildPersonalizedScopeSuggestions(params: {
  prompt: string;
  repoFullName?: string;
  branch?: string;
  repoSignals?: AgentExecuteRepoSignals;
  memoryEntries?: AgentSuperMemoryEntry[];
  scopeAnswers?: AgentScopeAnswers;
}): ScopeSuggestionBuildResult {
  const inferredSpecialization = detectPromptSpecialization(params.prompt);
  const goalAsSpecialization = params.scopeAnswers?.goal === "security"
    || params.scopeAnswers?.goal === "debugging"
    || params.scopeAnswers?.goal === "stability"
    || params.scopeAnswers?.goal === "performance"
    ? (params.scopeAnswers.goal as PromptSpecialization)
    : null;
  const specialization = goalAsSpecialization || inferredSpecialization;
  const repoContext = `${params.repoFullName || "this repository"} (${params.branch || "current branch"})`;
  const memoryHints = extractMemoryHints({
    memoryEntries: params.memoryEntries,
    repoFullName: params.repoFullName,
    prompt: params.prompt,
  });
  const repoStackHints = inferStackHintsFromRepoSignals(params.repoSignals);
  const stackHints = collectUnique([...memoryHints.stackHints, ...repoStackHints], 4);
  const shouldSuggestMonitoring =
    stackHints.includes("web") ||
    /\b(web|frontend|browser|pagespeed|lighthouse|core web vitals?)\b/i.test(
      `${params.prompt || ""} ${(params.repoSignals?.frameworkHints || []).join(" ")}`
    );
  const stackContext = stackHints.length > 0 ? ` Stack hints: ${stackHints.join(", ")}.` : "";
  const goalHint = buildGoalHint(params.scopeAnswers);
  const targetHint = buildTargetHint({
    scopeAnswers: params.scopeAnswers,
    fileAnchors: memoryHints.fileAnchors,
    areaHints: memoryHints.areaHints,
  });
  const verificationHint = buildVerificationHint(params.scopeAnswers);

  const templates: Record<"performance" | "security" | "debugging" | "stability", PromptScopeSuggestion[]> = {
    performance: [
      {
        id: "scope-performance-personalized",
        label: "Fix top performance bottlenecks",
        category: "performance",
        rationale: "Targets measurable speed improvements in existing files.",
        value: `In ${repoContext}, identify and fix the top 3 performance bottlenecks in existing code.${targetHint}${goalHint} Focus on concrete edits and explain why each change improves runtime behavior.${stackContext}${verificationHint}`,
      },
      {
        id: "scope-performance-pagespeed",
        label: "Improve PageSpeed outcomes",
        category: "performance",
        rationale: "Anchors execution to concrete metrics and file-level patches.",
        value: `Improve PageSpeed outcomes for ${repoContext}.${targetHint}${goalHint} Prioritize LCP/TTFB and bundle cost reductions in current modules.${stackContext}${verificationHint}`,
      },
      {
        id: "scope-performance-monitoring",
        label: "Patch + wire monitoring",
        category: "performance",
        rationale: "Pairs concrete code fixes with optional continuous monitoring.",
        value: `Improve web performance in ${repoContext}.${targetHint}${goalHint} Apply concrete file-level fixes, then connect or select Swayashin Monitoring for ongoing metric tracking.${stackContext}${verificationHint}`,
      },
    ],
    security: [
      {
        id: "scope-security-personalized",
        label: "Harden security hotspots",
        category: "security",
        rationale: "Prioritizes auth/session/input-validation vulnerabilities.",
        value: `Run a targeted security hardening pass on ${repoContext}.${targetHint}${goalHint} Focus on input validation, auth/session handling, and risky command or eval paths.${stackContext}${verificationHint}`,
      },
      {
        id: "scope-security-risk",
        label: "Patch highest-risk paths",
        category: "security",
        rationale: "Focuses on exploit-prone paths with concrete remediation.",
        value: `Find and patch the highest-risk code paths in ${repoContext}.${targetHint}${goalHint} Document risk level and exactly what attack path each fix closes.${stackContext}${verificationHint}`,
      },
    ],
    debugging: [
      {
        id: "scope-debug-personalized",
        label: "Debug and fix active failure",
        category: "debugging",
        rationale: "Focuses on reproducible failures and root-cause fixes.",
        value: `Debug and fix the active failing behavior in ${repoContext}.${targetHint}${goalHint} Identify root cause, patch affected files, and verify the failure no longer reproduces.${stackContext}${verificationHint}`,
      },
      {
        id: "scope-debug-regression",
        label: "Resolve recurring regression",
        category: "debugging",
        rationale: "Targets recurring errors with deterministic verification.",
        value: `Investigate and resolve the recurring regression in ${repoContext}.${targetHint}${goalHint} Prioritize the highest-impact failing path and provide proof of fix.${stackContext}${verificationHint}`,
      },
    ],
    stability: [
      {
        id: "scope-stability-reliability",
        label: "Stabilize flaky behavior",
        category: "stability",
        rationale: "Targets regressions, flaky behavior, and reliability gaps with concrete fixes.",
        value: `Run a stability hardening pass on ${repoContext}.${targetHint}${goalHint} Identify the top reliability risks, patch affected files, and validate the behavior is consistent across retries.${stackContext}${verificationHint}`,
      },
      {
        id: "scope-stability-regression",
        label: "Fix regressions and flaky paths",
        category: "stability",
        rationale: "Focuses on deterministic behavior and guards against repeat failures.",
        value: `Identify and fix regressions in ${repoContext}.${targetHint}${goalHint} Prioritize flaky paths, race conditions, and brittle state transitions, then verify with repeatable checks.${stackContext}${verificationHint}`,
      },
    ],
  };

  const order: Array<"performance" | "security" | "debugging" | "stability"> =
    specialization === "security"
      ? ["security", "stability", "debugging", "performance"]
      : specialization === "debugging"
      ? ["debugging", "stability", "performance", "security"]
      : specialization === "stability"
      ? ["stability", "debugging", "security", "performance"]
      : specialization === "performance"
      ? ["performance", "stability", "security", "debugging"]
      : ["performance", "security", "debugging", "stability"];
  const suggestions = order
    .flatMap((category) => templates[category])
    .filter((suggestion) =>
      suggestion.id === "scope-performance-monitoring" ? shouldSuggestMonitoring : true
    )
    .slice(0, 4);

  const targetAreaOptions = collectUnique(
    [
      ...(memoryHints.fileAnchors || []),
      ...(memoryHints.areaHints || []),
      ...(Array.isArray(params.repoSignals?.filesSample)
        ? params.repoSignals!.filesSample!.slice(0, 2)
        : []),
    ],
    6
  );

  return {
    specialization,
    suggestions,
    requiredHints: buildRequiredHints(specialization),
    memoryDerived: memoryHints.memoryDerived,
    answerOptions: {
      goal: ["performance", "security", "debugging", "stability"],
      targetArea: targetAreaOptions,
      verification: buildVerificationOptions(stackHints),
    },
  };
}

export function getRepoScopedMemoryEntries(
  entries: AgentSuperMemoryEntry[] | undefined,
  repoFullName?: string
): AgentSuperMemoryEntry[] {
  if (!Array.isArray(entries) || entries.length === 0) return [];
  const targetRepo = normalizeText(repoFullName || "");
  if (!targetRepo) return entries.slice(0, 24);
  const scoped = entries.filter((entry) => normalizeText(String(entry.repo || "")) === targetRepo);
  if (scoped.length > 0) return scoped.slice(0, 24);
  return entries.slice(0, 24);
}

export function isActionableIntakeWarning(warning: AgentIntakeWarning): boolean {
  return warning.code !== "heavy_scope_uncertain";
}
